<template>
  <div>
    <div :class="tableWrapperClass">
      <table
        v-if="values.length"
        :class="[$style.table, { [$style.tight]: tight, [$style.compact]: compact }]"
      >
        <thead ref="tableHead">
          <tr>
            <th v-for="(col, i) in head" :key="i" :style="headStyle(i)">
              <div v-if="showHead" :class="$style.cell">
                {{ col.value !== undefined ? col.value : col }}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-for="(row, rowIndex) in values" :key="rowIndex">
            <slot
              v-if="isInDom(rowIndex)"
              name="row"
              :row="row.rowData !== undefined ? row.rowData : row"
              :show="isDisplayed(rowIndex)"
              :isFirst="isFirst(rowIndex)"
              :isLast="isLast(rowIndex)"
              :isLastPrint="isLastPrint(rowIndex)"
              :rowId="uniqueRowId(row.rowId !== undefined ? row.rowId : rowIndex)"
            >
              <tr
                v-show="isDisplayed(rowIndex)"
                :class="[
                  $style.row,
                  {
                    [$style.rowFirst]: isFirst(rowIndex),
                    [$style.rowLast]: isLast(rowIndex),
                    [$style.rowLastPrint]: isLastPrint(rowIndex),
                  },
                ]"
              >
                <td
                  v-for="(cell, cellIndex) in row.rowData !== undefined ? row.rowData : row"
                  :key="cellIndex"
                >
                  <div :class="$style.cell">
                    <slot name="cell" :cell="cell" :i="cellIndex">
                      {{ cell }}
                    </slot>
                  </div>
                </td>
              </tr>
            </slot>
          </template>
          <tr
            v-if="values.length > 10 && !paginationDetach"
            :class="[$style.row, $style.rowLast, $style.rowPagination]"
          >
            <td :colspan="head.length">
              <div :class="$style.cell">
                <div :class="$style.controls">
                  <Pagination
                    v-model="currentPage"
                    :page-range="pageRange"
                    :page-count="pageCount"
                    :class="$style.pagination"
                  />
                  <ShowSelector
                    v-if="!compact"
                    :class="$style.pagesCount"
                    :selected="onPageSelected"
                    :values="onPageValues"
                    @select="changeOnpageCount"
                  />
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!values.length" :class="com.message">
      {{ notFoundMessage }}
    </div>

    <slot name="footer" />

    <div v-if="values.length > 10 && paginationDetach" :class="$style.controls">
      <Pagination
        v-model="currentPage"
        :page-range="pageRange"
        :page-count="pageCount"
        :class="$style.pagination"
      />
      <ShowSelector
        v-if="!compact"
        :class="$style.pagesCount"
        :selected="onPageSelected"
        :values="onPageValues"
        @select="changeOnpageCount"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import findIndex from 'lodash/findIndex';

import Pagination from './Pagination.vue';
import ShowSelector from './ShowSelector.vue';

export default defineComponent({
  components: { Pagination, ShowSelector },
  inject: ['printRef'],
  props: {
    head: { type: Array, required: true },
    notFoundMessage: { type: String, default: 'No data' },
    paginationDetach: { type: Boolean, default: false },
    showHead: { type: Boolean, default: true },
    values: { type: Array, required: true },
    tight: { type: Boolean, default: false },
    compact: { type: Boolean, default: false },
    hashName: { type: String, default: null },
    tableWrapperClass: { type: String, default: null }
  },
  data() {
    return {
      currentPage: 1,
      onPageSelected: 0,
      onPageValues: [10, 20, 30, 40, 60],
    };
  },
  computed: {
    onPage() {
      return this.onPageValues[this.onPageSelected];
    },
    pageCount() {
      return Math.ceil(this.values.length / this.onPage);
    },
    pageRange() {
      return this.tight ? 3 : 6;
    },
    additionalRowsCount() {
      return this.values.length <= 10 || this.paginationDetach ? 0 : 1;
    },
  },
  watch: {
    currentPage() {
      this.showTableStart();
    },
  },
  mounted() {
    window.addEventListener('hashchange', this.goToRowIfNeed, false);
    this.goToRowIfNeed();
  },
  beforeUnmount() {
    window.removeEventListener('hashchange', this.goToRowIfNeed, false);
  },
  methods: {
    uniqueRowId(rowId) {
      if (this.hashName !== null) {
        return `table-${this.hashName}-${rowId}`;
      }
    },
    goToRowIfNeed() {
      const start = `table-${this.hashName}-`;
      if (location.hash.includes(start)) {
        const rowId = location.hash.substring(location.hash.indexOf(start) + start.length);
        let index = findIndex(this.values, { rowId });
        if (index === -1) {
          index = parseInt(rowId);
        }
        if (Number.isInteger(index) && index >= 0) {
          this.currentPage = Math.floor(index / this.onPage) + 1;
          this.$nextTick(() => {
            const element = document.getElementById(`table-${this.hashName}-${rowId}`);
            if (element) element.scrollIntoView();
          });
        }
      }
    },
    showTableStart() {
      try {
        this.$refs.tableHead.scrollIntoViewIfNeeded(false);
      } catch {
        this.$refs.tableHead.scrollIntoView();
      }
    },
    changeOnpageCount(i) {
      this.onPageSelected = i;
      this.currentPage = 1;
      this.showTableStart();
    },
    isDisplayed(i) {
      return i >= (this.currentPage - 1) * this.onPage && i < this.currentPage * this.onPage;
    },
    isInDom(i) {
      return this.isDisplayed(i) || this.printRef.value;
    },
    isFirst(i) {
      return this.showHead === false && (i === (this.currentPage - 1) * this.onPage || i === 0);
    },
    isLast(i) {
      return (
        i === this.currentPage * this.onPage - 1 + this.additionalRowsCount
        || i === this.values.length - 1 + this.additionalRowsCount
      );
    },
    isLastPrint(i) {
      return i === this.values.length - 1;
    },
    headStyle(index) {
      return `width: ${this.head[index].width}px`;
    },
  },
});
</script>

<style lang="scss" module>
.table {
  border-collapse: collapse;
  $headColor: var(--table-head-color);

  $borderStyle: 1px solid var(--table-border-color);

  & > thead,
  & > tbody {
    & > tr {
      &.rowPagination {
        @media print {
          display: none;
        }
      }
      &:not(.rowPagination) {
        @media print {
          display: table-row !important;
        }
      }

      & > td,
      & > th {
        z-index: 1;
        position: relative;

        & > *::before {
          z-index: -1;
          display: block;
          position: absolute;
          content: '';
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        }

        & > .cell {
          white-space: pre-wrap;
          overflow-wrap: anywhere;
          word-break: break-word;

          display: block;
          height: 100%;
          padding-left: 10px;
        }

        &:first-child > .cell {
          padding-left: var(--padding-left-right, 20px);
          &::before {
            border-left: $borderStyle;
          }
        }
        &:last-child > .cell {
          padding-right: var(--padding-left-right, 20px);
          &::before {
            border-right: $borderStyle;
          }
        }
      }

      & > th {
        & > .cell {
          text-align: start;
          font-weight: 500;
          font-size: 13px;
          letter-spacing: 0.02em;
          color: rgba(63, 71, 87, 0.85);
          padding-top: 12px;
          padding-bottom: 12px;

          &::before {
            background-color: $headColor;
            border-bottom: $borderStyle;
            border-top: 1px solid var(--table-head-border-color);
          }
        }

        &:first-child > .cell::before {
          border-top-left-radius: 6px;
          border-top-color: var(--table-head-border-color);
          border-left-color: var(--table-head-border-color);
        }

        &:last-child > .cell::before {
          border-top-right-radius: 6px;
          border-top-color: var(--table-head-border-color);
          border-right-color: var(--table-head-border-color);
        }
      }

      & > td > .cell {
        font-size: 12px;
        color: #2f3543;
        padding-top: 10px;
        padding-bottom: 10px;

        &::before {
          border-bottom: $borderStyle;
        }
      }

      @media not print {
        &.rowLast > td {
          &:first-child > .cell::before {
            border-bottom-left-radius: 6px;
          }

          &:last-child > .cell::before {
            border-bottom-right-radius: 6px;
          }
        }

        &.rowFirst > td {
          & > .cell::before {
            border-top: $borderStyle;
          }

          &:first-child > .cell::before {
            border-top-left-radius: 6px;
          }

          &:last-child > .cell::before {
            border-top-right-radius: 6px;
          }
        }
      }
      @media print {
        &.rowLastPrint > td {
          &:first-child > .cell::before {
            border-bottom-left-radius: 6px;
          }
          &:last-child > .cell::before {
            border-bottom-right-radius: 6px;
          }
        }
      }
      &:nth-child(even) > td > .cell::before {
        background: var(--secondary-cell-color);
      }
    }
  }
  .controls {
    padding: 10px 0;
  }
}
.controls {
  display: flex;
  position: relative;
  justify-content: center;

  @media screen and (max-width: 870px) {
    padding-top: 10px;
    justify-content: flex-start;
    flex-wrap: wrap;
  }

  @media screen and (max-width: 620px) {
    padding-top: 45px;
  }
  
  @media print {
    display: none;
  }
}
.pagesCount {
  z-index: 3;
  position: absolute;
  right: 0;

  @media screen and (max-width: 620px) {
    top: 10px;
  }
}
.tight {
  .pagesCount {
    position: relative;
  }
  .controls {
    justify-content: space-between;
  }
}
.table + .controls {
  margin-top: 20px;
}

.compact {
  &.table {
    & > thead,
    & > tbody {
      & > tr {
        & > th {
          & > .cell {
            text-align: start;
            font-size: 13px;
            padding-top: 6px;
            padding-bottom: 6px;

            font-weight: 500;
            font-size: 12px;
            letter-spacing: 0.02em;

            color: #2f3543;
            &::before {
              border: 1px solid #e0eaf2;
              border-radius: 0;
            }
          }
        }
        &.rowLast > td {
          &:first-child > .cell::before {
            border-bottom-left-radius: 0px;
          }
          &:last-child > .cell::before {
            border-bottom-right-radius: 0px;
          }
        }
        &.rowLast:nth-child(odd) > td > .cell::before {
          border-bottom: 1px solid transparent;
        }

        &:nth-child(odd) > td > .cell::before {
          border-left-color: transparent;
          border-right-color: transparent;
        }
      }
    }
    & ~ .controls {
      margin-top: 10px;
      padding: 0px var(--padding-left-right, 20px);
      justify-content: flex-start;
    }
  }
}
</style>

<style lang="scss" src="./common.module.scss" module="com"></style>
